/**
 * License Guard
 *
 * Blocks navigation to module-gated routes when the active team has no
 * active license for the required module. Use `licenseGuardFor(module)` to
 * compose into a route's `canActivate` chain after the auth / email /
 * profile guards (so the active team is loaded by the time this runs).
 *
 * On denial the guard returns a `UrlTree` to `/license-expired?module=…`,
 * which is the canonical Angular pattern for "redirect, don't activate".
 *
 * @example
 * {
 *   path: 'notebook',
 *   canActivate: [authGuard, emailVerifiedGuard, profileGuard, licenseGuardFor('tools')],
 *   ...
 * }
 */

import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { LicenseModule } from 'src/app/core/schema';
import { AccountState } from 'src/app/store/account/account.state';

/** Returns a CanActivateFn that gates the route on an active license for `module`. */
export const licenseGuardFor = (module: LicenseModule): CanActivateFn => () => {
  const accountState = inject(AccountState);
  const router = inject(Router);

  // No team selected — send back to the team picker rather than the
  // license-expired page (the user has nothing to be expired against).
  if (!accountState.activeTeam()) {
    return router.parseUrl('/my-blume');
  }

  const hasAccess =
    module === 'tools' ? accountState.hasToolsAccess() : accountState.hasAcademyAccess();

  if (!hasAccess) {
    return router.parseUrl(`/license-expired?module=${module}`);
  }

  return true;
};
